/**
 * VerificationForm Component
 *
 * Hybrid form that displays AI-extracted values with editable text inputs.
 * Shows confidence badges and allows easy manual correction.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Input, Button } from '../ui';
import type { ExtractedId } from '../../lib/claudeVision';

export interface ExtractedField {
  value: string;
  confidence: 'high' | 'medium' | 'low';
  context?: string;
  wasEdited?: boolean;
}

export interface VerificationData {
  protocolId: ExtractedField;
  subjectId: ExtractedField;
  medId: ExtractedField;
  vialId?: ExtractedField;
}

interface VerificationFormProps {
  /** The captured document image (base64 or URL) */
  imageUrl?: string;
  /** Initial extracted values from AI */
  extractedData: Partial<VerificationData>;
  /** Whether this is teaching mode (first use of protocol) */
  isTeachingMode?: boolean;
  /** Whether AI extraction is still loading */
  isExtracting?: boolean;
  /** Whether to show vial ID field */
  showVialId?: boolean;
  /** Whether in single Med ID mode (only one ID allowed) */
  singleMode?: boolean;
  /** Callback when form is confirmed */
  onConfirm: (data: VerificationData) => void;
  /** Callback when user wants to go back */
  onBack?: () => void;
  /** Optional error message */
  error?: string;
}

function ConfidenceBadge({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  // Confidence states are domain colors — hues kept, dark pairs added
  const colors = {
    high: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    low: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  };

  const icons = {
    high: '\u2713', // checkmark
    medium: '?',
    low: '!'
  };

  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full ${colors[confidence]}`}>
      {icons[confidence]} {confidence.toUpperCase()}
    </span>
  );
}

function FieldInput({
  label,
  value,
  confidence,
  onChange,
  placeholder,
  isLoading,
  wasEdited
}: {
  label: string;
  value: string;
  confidence?: 'high' | 'medium' | 'low';
  onChange: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  wasEdited?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {wasEdited && (
            <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">(edited)</span>
          )}
        </label>
        {confidence && !isLoading && (
          <ConfidenceBadge confidence={confidence} />
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isLoading ? 'Extracting...' : placeholder}
          disabled={isLoading}
          className={`
            w-full px-2.5 py-1.5 border rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            transition-colors duration-150
            disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800
            dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400
            ${wasEdited ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'}
            ${isLoading ? 'animate-pulse' : ''}
          `}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 dark:border-primary-400"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export function VerificationForm({
  imageUrl,
  extractedData,
  isTeachingMode = false,
  isExtracting = false,
  showVialId = false,
  singleMode = false,
  onConfirm,
  onBack,
  error
}: VerificationFormProps) {
  // Initialize form state with extracted data
  const [formData, setFormData] = useState<VerificationData>({
    protocolId: { value: '', confidence: 'low' },
    subjectId: { value: '', confidence: 'low' },
    medId: { value: '', confidence: 'low' },
    vialId: showVialId ? { value: '', confidence: 'low' } : undefined
  });

  // Track which fields were manually edited
  const [editedFields, setEditedFields] = useState<Set<keyof VerificationData>>(new Set());

  // Update form when extracted data changes
  useEffect(() => {
    setFormData(prev => ({
      protocolId: extractedData.protocolId || prev.protocolId,
      subjectId: extractedData.subjectId || prev.subjectId,
      medId: extractedData.medId || prev.medId,
      vialId: showVialId ? (extractedData.vialId || prev.vialId) : undefined
    }));
  }, [extractedData, showVialId]);

  const handleFieldChange = useCallback((field: keyof VerificationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        wasEdited: true
      }
    }));
    setEditedFields(prev => new Set(prev).add(field));
  }, []);

  const handleConfirm = () => {
    // Mark all edited fields
    const finalData = { ...formData };
    editedFields.forEach(field => {
      if (finalData[field]) {
        finalData[field] = { ...finalData[field]!, wasEdited: true };
      }
    });
    onConfirm(finalData);
  };

  // Validation depends on mode: IXRS mode needs all three fields, Vial mode only needs vialId
  const isValid = showVialId
    ? formData.vialId?.value.trim() !== ''
    : formData.protocolId.value.trim() !== '' &&
      formData.subjectId.value.trim() !== '' &&
      formData.medId.value.trim() !== '';

  return (
    <div className="space-y-3">
      {/* Teaching Mode Banner - compact version */}
      {isTeachingMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 dark:bg-amber-900/20 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            <span className="font-medium">Teaching Mode:</span> First verification for this protocol. Please verify fields carefully.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 dark:bg-red-900/20 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Form Fields First (so they're visible without scrolling) */}
      <Card>
        <CardContent className="space-y-3 p-3">
          <h3 className="font-semibold text-gray-900 text-sm dark:text-gray-100">
            {isExtracting ? 'Extracting Data...' : 'Verify Information'}
          </h3>

            {/* Show IXRS fields when not in vial mode */}
            {!showVialId && (
              <>
                <FieldInput
                  label="Protocol ID"
                  value={formData.protocolId.value}
                  confidence={formData.protocolId.confidence}
                  onChange={(value) => handleFieldChange('protocolId', value)}
                  placeholder="e.g., ABC-123"
                  isLoading={isExtracting}
                  wasEdited={editedFields.has('protocolId')}
                />

                <FieldInput
                  label="Subject ID"
                  value={formData.subjectId.value}
                  confidence={formData.subjectId.confidence}
                  onChange={(value) => handleFieldChange('subjectId', value)}
                  placeholder="e.g., 001-0042"
                  isLoading={isExtracting}
                  wasEdited={editedFields.has('subjectId')}
                />

                <FieldInput
                  label="Medication ID"
                  value={formData.medId.value}
                  confidence={formData.medId.confidence}
                  onChange={(value) => handleFieldChange('medId', value)}
                  placeholder={singleMode ? "e.g., 12345" : "e.g., 12345 or for multiple: 12345, 12346, 12347"}
                  isLoading={isExtracting}
                  wasEdited={editedFields.has('medId')}
                />

                {singleMode ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enter the single medication ID from the IXRS document
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    For multiple medication IDs, separate with commas (e.g., 12345, 12346, 12347)
                  </p>
                )}
              </>
            )}

            {/* Show only Med ID field when in med verification mode */}
            {showVialId && formData.vialId && (
              <>
                <FieldInput
                  label="Med ID"
                  value={formData.vialId.value}
                  confidence={formData.vialId.confidence}
                  onChange={(value) => handleFieldChange('vialId', value)}
                  placeholder={singleMode ? "e.g., 12345" : "e.g., 12345 or for multiple: 12345, 12346, 12347"}
                  isLoading={isExtracting}
                  wasEdited={editedFields.has('vialId')}
                />

                {singleMode ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enter the single medication ID from the vial label
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    For multiple Med IDs, separate with commas (e.g., 12345, 12346, 12347)
                  </p>
                )}
              </>
            )}

            {!isExtracting && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Click any field to edit if needed.
              </p>
            )}

          {/* Action Buttons - inline with form */}
          <div className="flex gap-2 pt-2">
            {onBack && (
              <Button variant="outline" onClick={onBack} size="sm">
                &#8592; Back
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              disabled={!isValid || isExtracting}
              className="flex-1"
              size="sm"
            >
              Confirm & Continue &#8594;
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Image Preview - below form and smaller on mobile */}
      {imageUrl && (
        <Card className="overflow-hidden">
          <CardContent className="p-2">
            <p className="text-xs text-gray-500 mb-1 dark:text-gray-400">Captured Image</p>
            <img
              src={imageUrl}
              alt="Captured document"
              className="w-full h-auto max-h-32 sm:max-h-48 object-contain bg-gray-100 rounded dark:bg-gray-900"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default VerificationForm;
